import React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { Loader2 } from "lucide-react";

import LandingPage from "./components/LandingPage";
import Layout from "./components/Layout";
import SessionTimeoutManager from "./components/SessionTimeoutManager";
import {
  AuthProvider,
  useAuth,
  AppLockProvider,
  useAppLock,
  ThemeProvider,
  useTheme,
  LanguageProvider,
} from "./contexts";
import AppUnlockPage from "./pages/AppUnlockPage";
import {
  LoginPage,
  RegisterPage,
  ForgotPINPage,
  Dashboard,
  HistoryPage,
  PaymentCallbackPage,
  ProfilePage,
  SavedAccountsPage,
  AdminDashboard,
  QRCodePage,
  InsightsPage,
  BuyTokenPage,
  SubscriptionDashboardPage,
  RealTimeMonitoringPage,
  OrganizationDashboardPage,
  SmartAutomationPage,
  WalletPage,
  SetupPinPage,
  AboutPage,
  ServicesPage,
  HelpPage,
  ContactPage,
  PrivacyPage,
  TermsPage,
} from "./pages";

const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }
  if (!user) return <Navigate to="/welcome" replace />;
  if (user.needs_pin_setup) return <Navigate to="/setup-pin" replace />;
  return children;
};

const AdminRoute = ({ children }) => {
  const { user, loading } = useAuth();
  const { unlocked, loading: lockLoading, guardEnabled } = useAppLock();

  if (loading || lockLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }
  if (!user) return <Navigate to="/login" replace />;
  if (!user.is_admin) return <Navigate to="/" replace />;
  if (guardEnabled && !unlocked) {
    return <AppUnlockPage />;
  }
  return children;
};

const ToasterWithTheme = () => {
  const { theme } = useTheme();
  return (
    <Toaster
      position="top-center"
      toastOptions={{
        style:
          theme === "dark"
            ? {
                background: "#18181b",
                color: "#fafafa",
                border: "1px solid #27272a",
              }
            : {
                background: "#ffffff",
                color: "#09090b",
                border: "1px solid #e4e4e7",
              },
      }}
    />
  );
};

function App() {
  return (
    <ThemeProvider>
      <LanguageProvider>
        <AppLockProvider>
          <AuthProvider>
            <div className="App">
              <ToasterWithTheme />
              <BrowserRouter>
              <SessionTimeoutManager />
              <Routes>
                <Route path="/welcome" element={<LandingPage />} />
                <Route path="/about" element={<AboutPage />} />
                <Route path="/services" element={<ServicesPage />} />
                <Route path="/help" element={<HelpPage />} />
                <Route path="/contact" element={<ContactPage />} />
                <Route path="/privacy" element={<PrivacyPage />} />
                <Route path="/terms" element={<TermsPage />} />
                <Route path="/login" element={<LoginPage />} />
                <Route path="/register" element={<RegisterPage />} />
                <Route path="/forgot-pin" element={<ForgotPINPage />} />
                <Route path="/setup-pin" element={<SetupPinPage />} />

                <Route
                  path="/"
                  element={
                    <ProtectedRoute>
                      <Layout>
                        <Dashboard />
                      </Layout>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/buy"
                  element={
                    <ProtectedRoute>
                      <Layout>
                        <BuyTokenPage />
                      </Layout>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/history"
                  element={
                    <ProtectedRoute>
                      <Layout>
                        <HistoryPage />
                      </Layout>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/accounts"
                  element={
                    <ProtectedRoute>
                      <Layout>
                        <SavedAccountsPage />
                      </Layout>
                    </ProtectedRoute>
                  }
                />
                <Route path="/meters" element={<Navigate to="/monitoring" replace />} />
                <Route
                  path="/subscriptions"
                  element={
                    <ProtectedRoute>
                      <Layout>
                        <SubscriptionDashboardPage />
                      </Layout>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/monitoring"
                  element={
                    <ProtectedRoute>
                      <Layout>
                        <RealTimeMonitoringPage />
                      </Layout>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/organization"
                  element={
                    <ProtectedRoute>
                      <Layout>
                        <OrganizationDashboardPage />
                      </Layout>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/smart"
                  element={
                    <ProtectedRoute>
                      <Layout>
                        <SmartAutomationPage />
                      </Layout>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/wallet"
                  element={
                    <ProtectedRoute>
                      <Layout>
                        <WalletPage />
                      </Layout>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/profile"
                  element={
                    <ProtectedRoute>
                      <Layout>
                        <ProfilePage />
                      </Layout>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/qr"
                  element={
                    <ProtectedRoute>
                      <Layout>
                        <QRCodePage />
                      </Layout>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/insights"
                  element={
                    <ProtectedRoute>
                      <Layout>
                        <InsightsPage />
                      </Layout>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/admin"
                  element={
                    <AdminRoute>
                      <Layout>
                        <AdminDashboard />
                      </Layout>
                    </AdminRoute>
                  }
                />
                <Route path="/payment/callback" element={<PaymentCallbackPage />} />
                <Route path="*" element={<Navigate to="/welcome" replace />} />
              </Routes>
              </BrowserRouter>
            </div>
          </AuthProvider>
        </AppLockProvider>
      </LanguageProvider>
    </ThemeProvider>
  );
}

export default App;
